import { useQuery } from '@tanstack/react-query';
import { v4 as uuid } from 'uuid';

import { useAppState } from '../../AppStateProvider';
import type { Insight } from '../../domain/insight';
import { InsightType } from '../../domain/insightType';
import type { Opportunity } from '../../domain/opportunity';
import { BenchmarkCategory, BenchmarkMode } from '../../domain/opportunity';
import type { Signal, TrafficSignal } from '../../domain/signal';
import { InsightSignalType } from '../../domain/signal';
import type { SiteTrend } from '../../domain/siteTrend';
import { USER_SAM_ON_BOARDING_COMPLETED } from '../../domain/user';
import { queryClient } from '../../queryClient';
import { HttpClient } from '../../services/HttpClient';
import { snakeToCamel } from '../../services/snakeToCamel';
import { useCompanyQuery } from '../company/useCompanyQuery';
import { useUserQuery } from '../user/useUserQuery';

import { insightsApi } from './api';
import { INSIGHTS_QUERY_KEY, insightsQueryKey } from './insightsQueryKey';

type TrafficSignalDto = Omit<TrafficSignal, 'siteTrends'> & {
  siteTrends: Record<number, Omit<SiteTrend, 'insightType' | 'id'>[]>;
};

type SignalDto = Exclude<Signal, TrafficSignal> | TrafficSignalDto;

const fetchSiteTrendsInsights = async (companyDomain: string): Promise<SiteTrend[]> => {
  const siteTrends = await insightsApi.getSiteTrends(companyDomain);

  return siteTrends.map((t) => {
    const trendWithNonZeroValues = t.trend.filter((v) => v.value !== 0);
    return { ...t, insightType: InsightType.SiteTrends, id: uuid(), trend: trendWithNonZeroValues };
  });
};

export const fetchOpportunitiesInsights = async (companyDomain: string): Promise<Opportunity[]> => {
  const results = await insightsApi.getOpportunities(companyDomain);

  return results.map((o) => ({ ...o, insightType: InsightType.Opportunity, id: uuid() }));
};

const fetchSignalsInsights = async (companyDomain: string): Promise<Signal[]> => {
  const searchParams = new URLSearchParams({ domain: companyDomain });
  const response = await HttpClient.get<SignalDto[]>(
    `/sales-extension/api/insights/traffic-signals?${searchParams.toString()}`
  );

  const result = response
    .map((s) => ({ ...s, id: uuid(), insightType: InsightType.Signal }))
    .map((s) => snakeToCamel(s) as Signal)
    .filter((s) => s.type === InsightSignalType.Traffic)
    .flatMap((s) => {
      const signal = s as unknown as TrafficSignalDto;
      const trafficSignals = Object.entries(signal.siteTrends).map(([countryId, trends]) => {
        const siteTrends = trends.map((t) => {
          const trendWithNonZeroValues = t.trend.filter((v) => v.value !== 0);
          return { ...t, trend: trendWithNonZeroValues };
        });

        return { ...signal, id: uuid(), siteTrends, countries: [Number(countryId)] };
      });

      return trafficSignals as TrafficSignal[];
    });

  return result;
};

const getSortPriority = (insight: Insight) => {
  const baseOpportunityPriority = 3;
  const opportunityPriorities = [BenchmarkCategory.Traffic, BenchmarkCategory.Engagement, BenchmarkCategory.Growth];
  const baseSiteTrendsPriority = baseOpportunityPriority + opportunityPriorities.length * 2;
  const siteTrendsPriorities = [
    'total_estimated_visits',
    'bounce_rate',
    'total_estimated_unique',
    'visits',
    'pages_per_visit',
    'total_estimated_pageviews',
    'visit_duration',
  ];

  if (insight.insightType === InsightType.Signal) {
    return insight.isDefault ? 2 : 1;
  }
  if (insight.insightType === InsightType.Opportunity) {
    const modeFactor =
      (insight.benchmark.mode === BenchmarkMode.TopOpportunities ? 0 : 1) * opportunityPriorities.length;
    const categoryFactor = opportunityPriorities.indexOf(insight.benchmark.category);
    return baseOpportunityPriority + modeFactor + categoryFactor;
  }
  if (insight.insightType === InsightType.SiteTrends) {
    let priorityOffset = siteTrendsPriorities.findIndex((p) => insight.metric.endsWith(p));
    priorityOffset = priorityOffset === -1 ? siteTrendsPriorities.length : priorityOffset;
    return priorityOffset + baseSiteTrendsPriority;
  }

  return Infinity;
};

export const useInsightsQuery = () => {
  const { isOpen } = useAppState();
  const { domain: companyDomain = '' } = useCompanyQuery().data || {};
  const { userSettings } = useUserQuery().data || {};

  return useQuery<Insight[]>({
    queryKey: insightsQueryKey.insights(companyDomain),
    queryFn: async () => {
      const insights = await Promise.all([
        fetchSiteTrendsInsights(companyDomain),
        fetchOpportunitiesInsights(companyDomain),
        fetchSignalsInsights(companyDomain),
      ]);
      return insights.flat().sort((a, b) => getSortPriority(a) - getSortPriority(b));
    },
    enabled: isOpen && !!companyDomain && !!userSettings?.[USER_SAM_ON_BOARDING_COMPLETED],
  });
};

export type InsightsQuery = ReturnType<typeof useInsightsQuery>;

export const invalidateAllInsightsQueries = () => {
  queryClient.invalidateQueries({ queryKey: [INSIGHTS_QUERY_KEY] }).catch(console.error);
};
